import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import { useRouter } from 'next/router';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from '@emotion/react';
// eslint-disable-next-line import/no-extraneous-dependencies
import parsePhoneNumber from 'libphonenumber-js';
import { H2, H3 } from '../styled/typography';
import { Action, City, ModalState } from '../../models';
import {
  ModalContent,
  ModalContentWrapper,
  ModalTitle,
  ModalTitleWrapper,
} from '../common/Modal';
import { CloseSVG } from '../styled/Close';
import { initialModalState } from '../../models/modal';
import Input from '../form/input';
import { Form } from '../styled/Form';
import { CustomCheckBox } from '../form/form';
import { RedButton } from '../styled/Button';
import Calendar from '../calendar/calendar';
import { http } from '../../lib/api/http';
import { V2, V3, V4 } from '../../lib/api/urls';
import FireLogoSVG from '../../public/img/fire-logo.svg';
import PaymentMarksSVG from '../../public/img/payment-marks.svg';
import TinkoffSVG from '../../public/img/tinkoff.svg';
import wordDeclension from '../../utils/word-declension';
import { splitPriceByThree } from '../../utils';
import { TimeSelect } from '../form/select';
import {
  ACTION_TYPES,
  CATEGORIES_STATUS,
  EventCategory,
  EventDate,
  RESERVATION_TYPES,
} from '../../models/action';
import { emailValidator, phoneValidator } from '../../utils/validators';
import { DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import { ConfigItem } from '../../models/config';
import { useAuthorization } from '../../providers/authorization';
import {
  categoriesForSelectedDate,
  eventDateForCurrentDate,
  isDateRequired,
  isPhoneFieldRequired,
  isSurnameFieldRequired,
  QUOTA_REMAINDER_TYPES,
  quotaForSelectedDateByCategoryId,
} from './index.model';
import { useSeoAnalytics } from '../../providers/seo-analytics';
import plusImg from '../../public/img/plus.png';
import minusImg from '../../public/img/minus.png';
import { errorTextIfSaleIsCompleted } from '../../constants/events';
import { setLocalStorageItem } from '../../lib/api/browserStorage';
import { PhoneInput } from '../form/phone-input';

yup.addMethod(yup.string, 'email', emailValidator);
yup.addMethod(yup.string, 'phone', phoneValidator);

interface PaymentData {
  event: string | number;
  count_by_category: ({ category: string | number; count: number } | null)[];
  time: string | null;
  email: string;
  first_name: string;
  phone_number?: string;
  date: string | null;
  last_name?: string;
  payment_bank?: string;
}

const validationSchema = yup.object().shape({
  name: yup.string().required('Обязательное поле'),
  surname: yup.string().when('action', {
    is: (value: Action) => isSurnameFieldRequired(value),
    then: (schema) => schema.required('Обязательное поле'),
  }),
  email: yup.string().required('Обязательное поле').email(),
  phone: yup.string().when('action', {
    is: (value: Action) => isPhoneFieldRequired(value),
    then: (schema) =>
      schema
        .required('Обязательное поле')
        .min(10, 'Неправильно введён телефон')
        .max(20, 'Неправильно введён телефон')
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        .phone(),
  }),
});

export function BookingForm({
  setModalState,
  actionData,
  paymentData = null,
  isPayment = false,
  bankSelectProp = false,
  gbReccomendLink,
  currentCity,
  banOnSomeEmails,
  banEmails,
}: {
  setModalState: Dispatch<SetStateAction<ModalState>>;
  actionData: Action;
  paymentData?: PaymentData | null;
  isPayment?: boolean;
  bankSelectProp?: boolean;
  gbReccomendLink: string | null;
  currentCity: City;
  banOnSomeEmails: boolean;
  banEmails: ConfigItem | undefined;
}) {
  const router = useRouter();
  const seoAnalytics = useSeoAnalytics();
  const {
    register,
    formState: { errors, isValid },
    control,
    watch,
    setError,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      surname: '',
      phone: '',
      email: '',
      action: actionData,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });
  const theme = useTheme();
  const shortReservation =
    actionData.type === ACTION_TYPES.reservation &&
    actionData.reservation_type === RESERVATION_TYPES.shortened;
  const isCertificateTypeWithOpenDate =
    actionData.with_open_date && actionData.type === ACTION_TYPES.certificate;
  const [bank, setBank] = useState('');
  const [bankSelect, showBankSelect] = useState(bankSelectProp);
  const [smartbiletLoadStatus, setSmartbiletLoadStatus] = useState('');
  const [widgetSrc, setWidgetSrc] = useState('');
  const [time, setTime] = useState<string | null>('');
  const [check, setCheck] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [permittedDates, setPermittedDates] = useState<string[] | undefined>(
    undefined
  );
  const [eventDates, setEventDates] = useState<EventDate[] | undefined>(
    undefined
  );
  const [orderTickets, setOrderTickets] = useState<{ [key: number]: number }>(
    {}
  );

  const auth = useAuthorization();

  useEffect(() => {
    // checking for excluded emails
    const subscription = watch((value, { name }) => {
      if (name === 'email' && value.email && value.email.length > 0) {
        if (
          banOnSomeEmails &&
          banEmails &&
          banEmails.value &&
          typeof banEmails.value === 'string'
        ) {
          const banEmailsArr = banEmails.value.split(' ');
          for (let i = 0; i < banEmailsArr.length; i += 1) {
            if (value.email.includes(`@${banEmailsArr[i]}`)) {
              return setTimeout(() => {
                setError('email', {
                  message: `Пожалуйста, используйте другую почту, на @${banEmailsArr[i]} билеты могут не прийти`,
                });
              });
            }
          }
        }
      }
      return null;
    });
    return () => subscription.unsubscribe();
  }, [watch, banOnSomeEmails, banEmails]);

  const getDates = (): [string, string] | null => {
    if (selectedDate) {
      const date = DateTime.fromFormat(selectedDate, 'dd.LL.yyyy').toFormat(
        'yyyy-LL-dd'
      );
      return [date, date];
    }
    return null;
  };

  function getTicketsCountText(category: EventCategory) {
    const selectedEventDate = eventDateForCurrentDate({ eventDates, time });
    const categoryItem: EventCategory = {
      ...category,
      ...quotaForSelectedDateByCategoryId(category.id, selectedEventDate),
    };
    // eslint-disable-next-line consistent-return
    if (categoryItem.status === CATEGORIES_STATUS.CLOSE_VISIBLE) return '';
    // eslint-disable-next-line consistent-return
    if (!categoryItem.show_remainder) return '';
    // eslint-disable-next-line consistent-return
    if (categoryItem.buy_form_text) return categoryItem.buy_form_text;
    const count = categoryItem?.remainder;
    if (count >= 100 || categoryItem.is_unlimited) {
      // eslint-disable-next-line consistent-return
      return 'Осталось больше <b>100</b> билетов';
    }
    if (count === 0) {
      // eslint-disable-next-line consistent-return
      return QUOTA_REMAINDER_TYPES.noTickets;
    }
    // eslint-disable-next-line consistent-return
    return `
        ${wordDeclension(count, 'Остался', 'Осталось', 'Осталось')}
    <b>${count}</b> ${wordDeclension(count, 'билет', 'билета', 'билетов')}`;
  }

  const decreaseItem = (category: EventCategory) => {
    const selectedEventDate = eventDateForCurrentDate({ eventDates, time });
    const categoryItem: EventCategory = {
      ...category,
      ...quotaForSelectedDateByCategoryId(category.id, selectedEventDate),
    };
    if (categoryItem.status === CATEGORIES_STATUS.CLOSE_VISIBLE) {
      return;
    }
    if (!orderTickets[categoryItem.id] || orderTickets[categoryItem.id] === 0) {
      return;
    }
    if (orderTickets[categoryItem.id]) {
      setOrderTickets((prev: any) => ({
        ...prev,
        [categoryItem.id]: prev[categoryItem.id] - 1,
      }));
    }
  };

  const increaseItem = (category: EventCategory) => {
    const selectedEventDate = eventDateForCurrentDate({ eventDates, time });
    const categoryItem: EventCategory = {
      ...category,
      ...quotaForSelectedDateByCategoryId(category.id, selectedEventDate),
    };
    if (selectedEventDate) {
      const dateQuota = selectedEventDate.quotas.find(
        (quota) => quota.category.id === category.id
      );
      if (dateQuota) {
        categoryItem.remainder = dateQuota.remainder;
        categoryItem.status = dateQuota.status;
      }
    }
    if (categoryItem.status === CATEGORIES_STATUS.CLOSE_VISIBLE) {
      return;
    }
    const maxCountArray = [actionData.max_count_per_user];
    if (!categoryItem.is_unlimited) {
      maxCountArray.push(categoryItem.remainder);
    }
    const maxCount = Math.min(...maxCountArray);
    if (maxCount === 0) {
      return;
    }
    if (orderTickets[categoryItem.id] === maxCount) {
      return;
    }
    if (orderTickets[categoryItem.id]) {
      setOrderTickets((prev: any) => ({
        ...prev,
        [categoryItem.id]: prev[categoryItem.id] + 1,
      }));
    } else {
      setOrderTickets((prev: any) => ({
        ...prev,
        [categoryItem.id]: 1,
      }));
    }
  };

  const serviceCoefficient = () =>
    (Number(actionData.cerificate_service_fee) + 100) / 100;

  const onSubscribe = (valuesData: PaymentData) => {
    if (check) {
      const subscriptionsData: {
        first_name: string;
        last_name?: string;
        email: string;
        phone_number?: string;
        city: string;
        source: string;
        event_id: number;
      } = {
        first_name: valuesData.first_name,
        last_name: valuesData.last_name,
        email: valuesData.email,
        phone_number: valuesData.phone_number,
        source: 'actionReservation',
        city: currentCity.remote_id,
        event_id: actionData.id,
      };

      if (valuesData.last_name) {
        subscriptionsData.last_name = valuesData.last_name;
      }
      if (valuesData.phone_number) {
        subscriptionsData.phone_number = valuesData.phone_number;
      }

      http.post(V2.subscriptions, subscriptionsData);
    }
  };

  function onSubmit() {
    const { name, phone, email, surname } = watch();
    const activeIndex = actionData.event_categories.findIndex(
      (category) => category.is_active
    );
    const data: PaymentData = paymentData || {
      event: actionData.id,
      count_by_category: shortReservation
        ? actionData.event_categories
            .map((_item, index) => {
              if (index === activeIndex) {
                return { category: _item.id, count: 1 };
              }
              return null;
            })
            .filter((item) => item !== null)
        : actionData.event_categories
            .map((item) => {
              if (orderTickets[item.id]) {
                return { category: item.id, count: orderTickets[item.id] };
              }
              return null;
            })
            .filter((item) => item !== null),
      time: isDateRequired({ isCertificateTypeWithOpenDate, shortReservation })
        ? time
        : null,
      email,
      first_name: name,
      date: isDateRequired({ isCertificateTypeWithOpenDate, shortReservation })
        ? DateTime.fromFormat(selectedDate!, 'dd.LL.yyyy').toFormat(
            'yyyy-LL-dd'
          )
        : null,
    };
    if (isSurnameFieldRequired(actionData) && !paymentData?.last_name) {
      data.last_name = surname;
    }
    if (bank) {
      data.payment_bank = bank;
    }
    if (isPhoneFieldRequired(actionData)) {
      const phoneNumber = parsePhoneNumber(
        paymentData?.phone_number || phone,
        'RU'
      );
      if (phoneNumber) {
        data.phone_number = phoneNumber.number.substring(1);
      }
    }
    seoAnalytics.dispatch!({ ym: ['Book_success'] });

    onSubscribe(data);

    http
      .post(V4.reservations, data, null, {
        contentType: 'json',
        withTokenRequest: true,
      })
      .then((response) => {
        setLocalStorageItem(ACTION_TYPES.reservation, response.data.token);
        if (
          actionData.type === ACTION_TYPES.reservation ||
          actionData.type === ACTION_TYPES.FREE
        ) {
          if (response.status === 400) {
            setModalState({
              show: true,
              content: (
                <ModalContentWrapper>
                  <ModalTitleWrapper setModalState={setModalState}>
                    <ModalTitle>
                      <span
                        css={{
                          fontWeight: 800,
                          lineHeight: '1.39',
                        }}
                      >
                        {response.data[0] === errorTextIfSaleIsCompleted
                          ? 'Продажа завершена'
                          : 'Ошибка'}
                      </span>
                    </ModalTitle>
                  </ModalTitleWrapper>
                  <ModalContent>
                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: '1.43',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: response.data[0],
                      }}
                    />
                  </ModalContent>
                </ModalContentWrapper>
              ),
              options: { width: 400 },
              enableOutsideClick: false,
            });
          } else {
            setModalState({
              show: true,
              content: (
                <ModalContentWrapper>
                  <ModalTitleWrapper
                    showClose={false}
                    setModalState={setModalState}
                  >
                    <ModalTitle>{response.data.title}</ModalTitle>
                  </ModalTitleWrapper>
                  <ModalContent>
                    <div css={{ textAlign: 'center', height: 125 }}>
                      <svg
                        css={{
                          width: 120,
                          height: 120,
                          marginLeft: -20,
                          transform: 'rotate(-20deg)',
                          fill: theme.colors.red,
                          overflow: 'hidden',
                        }}
                      >
                        <use xlinkHref='#envelope' />
                      </svg>
                    </div>
                    <div
                      css={{
                        padding: '1em 0 2em',
                        p: {
                          fontSize: 14,
                          fontWeight: 400,
                          marginBottom: 24,
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: response.data.description.html,
                      }}
                    />
                    <div css={{ textAlign: 'center' }}>
                      <RedButton
                        callback={() => {
                          setModalState(initialModalState);
                          if (gbReccomendLink) {
                            router.push(gbReccomendLink);
                          }
                        }}
                      >
                        Отлично
                      </RedButton>
                    </div>
                  </ModalContent>
                </ModalContentWrapper>
              ),
              outsideCloseCallback: () => {
                setModalState(initialModalState);
                if (gbReccomendLink) {
                  router.push(gbReccomendLink);
                }
              },
            });
          }
        } else if (actionData.type === ACTION_TYPES.certificate) {
          if (response.status === 400) {
            setModalState({
              show: true,
              content: (
                <ModalContentWrapper>
                  <ModalTitleWrapper setModalState={setModalState}>
                    <ModalTitle>
                      <span
                        css={{
                          fontWeight: 800,
                          lineHeight: '1.39',
                        }}
                      >
                        {response.data[0] === errorTextIfSaleIsCompleted
                          ? 'Продажа завершена'
                          : 'Ошибка'}
                      </span>
                    </ModalTitle>
                  </ModalTitleWrapper>
                  <ModalContent>
                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: '1.43',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: response.data[0],
                      }}
                    />
                  </ModalContent>
                </ModalContentWrapper>
              ),
              options: { width: 400 },
              enableOutsideClick: false,
            });
          } else {
            window.location.href = response.data.payment_url;
          }
        } else {
          setModalState(initialModalState);
          const url = new URL(window.location.href);
          url.searchParams.delete('order');
          router.replace(url.href, undefined, { shallow: true });
        }
      });
  }

  const choosePayment = () => {
    const { name, email, surname, phone } = watch();
    const data: PaymentData = {
      event: actionData.id,
      count_by_category: actionData.event_categories
        .map((item) => {
          if (orderTickets[item.id]) {
            return { category: item.id, count: orderTickets[item.id] };
          }
          return null;
        })
        .filter((item) => item !== null),
      time: isDateRequired({ isCertificateTypeWithOpenDate, shortReservation })
        ? time
        : null,
      email,
      first_name: name,
      date: isDateRequired({ isCertificateTypeWithOpenDate, shortReservation })
        ? DateTime.fromFormat(selectedDate!, 'dd.LL.yyyy').toFormat(
            'yyyy-LL-dd'
          )
        : null,
    };
    if (isSurnameFieldRequired(actionData)) {
      data.last_name = surname;
    }
    if (isPhoneFieldRequired(actionData)) {
      const phoneNumber = parsePhoneNumber(phone, 'RU');
      if (phoneNumber) {
        data.phone_number = phoneNumber.number;
      }
    }
    onSubscribe(data);

    setModalState((old) => ({
      ...old,
      show: true,
      options: { width: 760 },
      content: (
        <BookingForm
          gbReccomendLink={gbReccomendLink}
          setModalState={setModalState}
          actionData={actionData}
          bankSelectProp={bankSelect}
          paymentData={data}
          isPayment
          currentCity={currentCity}
          banOnSomeEmails={banOnSomeEmails}
          banEmails={banEmails}
        />
      ),
      outsideCloseCallback: () => {
        const url = new URL(window.location.href);
        url.searchParams.delete('order');
        router.replace(url.href, undefined, { shallow: true });
      },
    }));
  };

  const getPrice = () =>
    Object.entries(orderTickets).reduce((acc, cur) => {
      let discount = 0;
      const eventData = actionData.event_categories.find(
        (c) => c.id === Number(cur[0])
      );
      if (eventData) {
        if (eventData.discount !== null && Number(eventData.discount) !== 0) {
          discount = Number(eventData.discount);
        }
        let price = Number(eventData.price);
        if (discount) {
          price = (price * (100 - discount)) / 100;
        }
        return acc + price * (cur[1] as number);
      }
      return acc;
    }, 0);

  const totalPrice = () => {
    const coefficient = serviceCoefficient();
    const price = getPrice();

    return splitPriceByThree((price * coefficient).toFixed(2));
  };

  const totalServicePrice = () => {
    const price = getPrice();

    return (price * serviceCoefficient() - price).toFixed(2);
  };

  const getDayTime = () =>
    eventDates
      ?.filter((date) => !!date)
      ?.map((date) => DateTime.fromISO(date.time[0]).toFormat('HH:mm'));

  function timeOptionsOfSelectedDay(
    date: string | null,
    dates: EventDate[] | undefined
  ) {
    if (!date || !dates?.length) {
      return [];
    }
    return dates
      .map((eventDate) => eventDate.time[0])
      .map((item) =>
        item ? DateTime.fromFormat(item, 'TT').toFormat('T') : null
      );
  }

  function isTimeOptionsIncludesNull() {
    return timeOptionsOfSelectedDay(selectedDate, eventDates).includes(null);
  }

  function canShowTimeSelect() {
    const timeOptionsLength = timeOptionsOfSelectedDay(
      selectedDate,
      eventDates
    ).length;
    return (
      timeOptionsLength > 1 ||
      (timeOptionsLength === 1 && !isTimeOptionsIncludesNull())
    );
  }

  function getReservationPostData() {
    const reservationPostData: any = {
      event: actionData.remote_id,
    };
    const user = auth && auth.getUser();
    if (user) {
      Object.assign(reservationPostData, {
        email: user.email,
        phone_number: user.phone,
      });
    }
    if (user && user.first_name) {
      Object.assign(reservationPostData, {
        first_name: user.first_name,
        last_name: user.last_name,
      });
    }
    return reservationPostData;
  }
  function buySmartBilet() {
    const utmParams = Object.keys(router.query)
      .filter((i) => i.includes('utm_') || !i.includes('yclid'))
      .reduce((acc: any[], cur) => {
        acc.push(`${cur}=${router.query[cur]}`);
        return acc;
      }, []);

    const user = auth?.getUser();

    const userParams =
      user &&
      ['first_name', 'phone_number', 'email']
        .map((i) => ({
          k: `user_${i}`,
          v: encodeURIComponent(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            user[i] === Object(user[i])
              ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                user[i][Object.keys(user[i])[0]]
              : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                user[i]
          ),
        }))
        .filter((i) => !!i.v && i.v !== 'null')
        .map((i) =>
          i.k === 'user_phone_number' ? { ...i, v: i.v ? `+7${i.v}` : '' } : i
        )
        .reduce((acc, cur) => {
          switch (cur.k) {
            case 'user_phone_number':
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              acc.push(`user_phone=${cur.v[cur.k] || cur.v}`);
              break;
            case 'user_first_name':
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              acc.push(`user_name=${cur.v[cur.k] || cur.v}`);
              break;
            default:
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              acc.push(`${cur.k}=${cur.v[cur.k] || cur.v}`);
          }
          return acc;
        }, []);
    setWidgetSrc(
      actionData.smartbilet_url +
        (!!utmParams && !!utmParams.length ? `&${utmParams.join('&')}` : '') +
        (!!userParams && !!userParams.length ? `&${userParams.join('&')}` : '')
    );
  }
  function showSmartBiletForm() {
    let smartBiletUrl = '';
    http
      .post(V3.reservations, getReservationPostData(), null, {
        contentType: 'json',
        withTokenRequest: true,
      })
      .then((response) => {
        smartBiletUrl = `${actionData.smartbilet_url}&reservation_id=${response.data.id}`;
        return http.get(V2.smartbiletCheck, {
          smartbilet_url: smartBiletUrl,
        });
      })
      .then((response) => {
        const responseText = response.replace(/"/g, '');
        setSmartbiletLoadStatus(responseText);
        if (responseText === 'OK') {
          buySmartBilet();
        }
      })
      .catch(() => {
        setSmartbiletLoadStatus(`ERROR`);
      });
  }

  useEffect(() => {
    const user = auth?.getUser();

    if (user) {
      reset({
        name: user.first_name,
        phone: user.phone_number || '',
        email: user.email,
        surname: user.last_name || '',
        action: actionData,
      });
    }
  }, [auth, reset, actionData]);

  useEffect(() => {
    async function fetch() {
      if (selectedDate) {
        const eventDatesData = await http.get(V3.eventDates, {
          limit: 9e9,
          event: actionData.id,
          start_date: DateTime.fromFormat(selectedDate, 'dd.LL.yyyy').toFormat(
            'yyyy-LL-dd'
          ),
        });
        setEventDates(eventDatesData.results);
        if (eventDatesData.results.length) {
          const timeOptions = timeOptionsOfSelectedDay(
            selectedDate,
            eventDatesData.results
          );
          if (timeOptions.length === 1) {
            setTime(timeOptions[0]);
          } else {
            setTime('');
          }
        }
      }
    }
    fetch();

    setCalendarOpen(false);
  }, [selectedDate]);

  useEffect(() => {
    async function fetch() {
      const configData = await http.get(V2.config).then((data) => ({
        isSber: data.find((elem: ConfigItem) => elem.name === 'IS_SBER_ACTIVE')
          .value,
        isTinkoff: data.find(
          (elem: ConfigItem) => elem.name === 'IS_TINKOFF_ACTIVE'
        ).value,
      }));

      if (configData.isTinkoff && !configData.isSber) {
        setBank('tinkoff');
      } else if (!configData.isTinkoff && configData.isSber) {
        setBank('sber');
      } else if (configData.isTinkoff && configData.isSber) {
        showBankSelect(true);
      } else {
        setBank('tinkoff');
      }
    }
    fetch();
  }, []);

  useEffect(() => {
    async function fetch() {
      if (isCertificateTypeWithOpenDate) {
        const configData = await http.get(V2.config).then((data) => ({
          isSber: data.find(
            (elem: ConfigItem) => elem.name === 'IS_SBER_ACTIVE'
          ).value,
          isTinkoff: data.find(
            (elem: ConfigItem) => elem.name === 'IS_TINKOFF_ACTIVE'
          ).value,
        }));

        if (configData.isTinkoff && !configData.isSber) {
          setBank('tinkoff');
        } else if (!configData.isTinkoff && configData.isSber) {
          setBank('sber');
        } else if (configData.isTinkoff && configData.isSber) {
          showBankSelect(true);
        } else {
          setBank('tinkoff');
        }
      }
    }
    fetch();
  }, [isCertificateTypeWithOpenDate]);

  useEffect(() => {
    async function fetch() {
      if (actionData.type === ACTION_TYPES.purchase) {
        showSmartBiletForm();
        return;
      }

      if (actionData.nearest_event?.start_date) {
        const { month, year } = DateTime.fromFormat(
          actionData.nearest_event.start_date,
          'yyyy-LL-dd'
        );

        const dates = await http.get(V3.eventDatesCalendar, {
          event: actionData.id,
          month,
          year,
        });

        const eventDatesData = await http.get(V3.eventDates, {
          limit: 9e9,
          event: actionData.id,
          start_date: DateTime.now().toFormat('yyyy-LL-dd'),
        });

        setPermittedDates([...dates.results]);
        setEventDates(eventDatesData.results);
      }
    }
    fetch();
  }, [actionData]);

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = 'visible';
    };
  }, []);

  useEffect(() => {
    if (bank && bankSelect) onSubmit();
  }, [bank]);

  return (
    <ModalContentWrapper>
      <div>
        <ModalTitleWrapper
          setModalState={setModalState}
          customClose={
            <CloseSVG
              onClick={() => {
                setModalState(initialModalState);
                const url = new URL(window.location.href);
                url.searchParams.delete('order');
                router.replace(url.href, undefined, { shallow: true });
              }}
              css={{ width: 20, height: 20 }}
            />
          }
        >
          {actionData.type !== ACTION_TYPES.purchase && !isPayment ? (
            <H2
              css={{
                fontSize: 26,
                fontWeight: 700,
                fontFamily: 'Montserrat',
                color: '#282828',
              }}
            >
              {actionData.reservation_type === RESERVATION_TYPES.shortened
                ? 'Заявка'
                : actionData.type === ACTION_TYPES.certificate
                ? 'Покупка'
                : 'Бронирование'}
            </H2>
          ) : (
            <H2
              css={{
                fontSize: 26,
                fontWeight: 700,
                fontFamily: 'Montserrat',
                color: '#282828',
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  marginTop: 10,
                  marginLeft: -10,
                  marginRight: -10,
                },
              }}
            >
              {isPayment ? 'Выберите способ оплаты' : ''}
            </H2>
          )}
        </ModalTitleWrapper>
      </div>
      <ModalContent>
        {!isPayment ? (
          <>
            {actionData.type === ACTION_TYPES.purchase &&
              !!smartbiletLoadStatus &&
              smartbiletLoadStatus === 'OK' && (
                <iframe
                  title='SmartBilet'
                  src={widgetSrc}
                  css={{
                    border: 'none',
                    height: '75vh',
                    width: '100%',
                  }}
                />
              )}
            {actionData.type === ACTION_TYPES.purchase &&
              !!smartbiletLoadStatus &&
              smartbiletLoadStatus === 'ERROR' && (
                <div css={{ textAlign: 'center' }}>
                  <H3>Что-то пошло не так, попробуйте позже</H3>
                </div>
              )}

            {actionData.type !== ACTION_TYPES.purchase && (
              <div>
                <Form
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <Input
                    text='Имя'
                    name='name'
                    error={!!errors.name}
                    errorMessage={errors.name?.message}
                    control={control}
                    registerProps={register('name')}
                  />
                  {isSurnameFieldRequired(actionData) && (
                    <Input
                      text='Фамилия'
                      name='surname'
                      error={!!errors.surname}
                      errorMessage={errors.surname?.message}
                      control={control}
                      registerProps={register('surname')}
                    />
                  )}
                  {isPhoneFieldRequired(actionData) && (
                    <PhoneInput
                      control={control}
                      name='phone'
                      error={!!errors.phone}
                      errorMessage={errors.phone?.message}
                    />
                  )}
                  <Input
                    text='E-mail'
                    name='email'
                    control={control}
                    registerProps={register('email')}
                    error={!!errors.email}
                    errorMessage={errors.email?.message}
                  />
                  {/* CALENDAR */}
                  {isDateRequired({
                    isCertificateTypeWithOpenDate,
                    shortReservation,
                  }) &&
                    !!permittedDates?.length && (
                      <div
                        css={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          borderBottom: '1px solid #DCDEDA',
                          height: 34,
                          cursor: 'pointer',
                          position: 'relative',
                        }}
                        onClick={() => setCalendarOpen((prev) => !prev)}
                      >
                        {selectedDate ? (
                          <span>
                            {DateTime.fromFormat(selectedDate, 'dd.LL.yyyy')
                              .setLocale('ru')
                              .toFormat('dd MMMM')}
                          </span>
                        ) : (
                          <span css={{ color: '#BDBFBB' }}>Выберите дату</span>
                        )}
                        <svg
                          css={{
                            width: 20,
                            height: 20,
                            fill: '#f44336',
                          }}
                        >
                          <use xlinkHref='#calendar' />
                        </svg>
                        <div
                          css={{
                            position: 'absolute',
                            background: '#fff',
                            top: 34,
                            right: 0,
                            width: '100%',
                            maxWidth: 387,
                            overflow: 'hidden',
                            transition: '.2s',
                            zIndex: 10,
                            boxShadow: `0 4px 15px rgba(0, 0, 0, 0.2)`,
                            ...(calendarOpen
                              ? { maxHeight: 330 }
                              : { maxHeight: 0 }),
                          }}
                        >
                          <Calendar
                            additionalCss={{ padding: '24px 0' }}
                            enableMultiSelection={false}
                            latestEventDate={actionData.latest_event}
                            nearestEventDate={actionData.nearest_event}
                            initPermittedDates={permittedDates}
                            onDatePicked={(values) => {
                              if (values) {
                                setSelectedDate(values[0]);
                              }
                            }}
                            onMonthChanged={async (month, year) =>
                              http
                                .get(V3.eventDatesCalendar, {
                                  event: actionData.id,
                                  month,
                                  year,
                                })
                                .then((res) => res.results)
                            }
                            initDates={getDates()}
                          />
                        </div>
                      </div>
                    )}
                  {/* TIME SELECT */}
                  {canShowTimeSelect() && (
                    <TimeSelect
                      options={getDayTime()}
                      value={time}
                      setValue={setTime}
                    />
                  )}
                </Form>
                {/* PLACE */}
                {selectedDate &&
                  eventDateForCurrentDate({ eventDates, time }) && (
                    <div
                      css={{
                        padding: 8,
                        background: '#F8F8F8',
                        marginTop: 12,
                        borderRadius: 4,
                      }}
                    >
                      <p
                        css={{
                          color: '#BFC1C3',
                          fontSize: 12,
                          lineHeight: '130%',
                        }}
                      >
                        Место
                      </p>
                      <p
                        css={{
                          color: '#282828',
                          paddingRight: 8,
                          lineHeight: '160%',
                          marginTop: 2,
                        }}
                      >
                        {
                          eventDateForCurrentDate({ eventDates, time })?.place
                            .title
                        }
                      </p>
                    </div>
                  )}
                {/* CATEGORIES */}
                {categoriesForSelectedDate({
                  selectedDate,
                  eventDates,
                  time,
                  action: actionData,
                  isCertificateTypeWithOpenDate,
                }).map((item: EventCategory) => (
                  <div css={{ marginTop: 12 }} key={`buy-buttons-${item.id}`}>
                    <div
                      css={{
                        position: `relative`,
                        width: `100%`,
                        display: `flex`,
                        flexWrap: `wrap`,
                        justifyContent: `space-between`,
                        alignItems: `center`,
                        backgroundColor: `transparent`,
                        fontSize: `1rem`,
                        lineHeight: `1`,
                        minHeight: `34px`,
                        fontWeight: `400`,
                        borderBottom: '1px solid #BFC1C3',
                        ...(getTicketsCountText(item) ===
                        QUOTA_REMAINDER_TYPES.noTickets
                          ? { opacity: 0.3 }
                          : {}),
                      }}
                    >
                      <p
                        css={{
                          color: '#282828',
                        }}
                      >
                        {item.title}
                      </p>
                      <div
                        css={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          gap: 15,
                          height: 28,
                          marginLeft: 'auto',
                        }}
                      >
                        <span>{splitPriceByThree(item.price)} ₽</span>
                        <div
                          css={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            color: '#7D7B7A',
                            button: {
                              width: 28,
                              height: 28,
                              backgroundSize: '16px',
                              backgroundPosition: '50%',
                              backgroundRepeat: 'no-repeat',
                              color: '#BFC1C3',
                            },
                          }}
                        >
                          <button
                            type='button'
                            onClick={() => decreaseItem(item)}
                            css={{
                              backgroundImage: `url(${minusImg.src})`,
                            }}
                          >
                            {' '}
                          </button>
                          <span>
                            {(!!orderTickets && orderTickets[item.id]) || 0}
                          </span>
                          <button
                            type='button'
                            onClick={() => increaseItem(item)}
                            css={{
                              backgroundImage: `url(${plusImg.src})`,
                            }}
                          >
                            {' '}
                          </button>
                        </div>
                      </div>
                    </div>
                    {!!getTicketsCountText(item) && (
                      <div
                        css={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginTop: 12,
                        }}
                      >
                        <FireLogoSVG css={{ width: 12 }} />
                        <span
                          css={{
                            fontSize: 12,
                            fontWeight: 400,
                            fontFamily: 'Golos',
                            color: '#282828',
                            paddingTop: 6,
                            b: {
                              fontWeight: 'bolder',
                            },
                          }}
                          dangerouslySetInnerHTML={{
                            __html: getTicketsCountText(item),
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
                <div css={{ marginTop: 16 }}>
                  {actionData.type === ACTION_TYPES.certificate && (
                    <p
                      css={{
                        fontSize: 18,
                        fontWeight: 700,
                        fontFamily: 'Montserrat',
                        color: '#282828',
                      }}
                    >
                      Итого {totalPrice()} ₽
                    </p>
                  )}
                  {actionData.type === ACTION_TYPES.certificate && (
                    <p
                      css={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: '#7D7B7A',
                      }}
                    >
                      Сервисный сбор {totalServicePrice()} ₽
                    </p>
                  )}
                  <div
                    css={{
                      marginTop: 35,
                      marginBottom: 14,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      fontSize: 14,
                      color: '#7D7B7A',
                      p: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      },
                    }}
                  >
                    {actionData.service_fee === 0 && (
                      <p>
                        <svg
                          css={{
                            width: 20,
                            height: 8,
                            fill: '#fff',
                          }}
                        >
                          <use xlinkHref='#zero-percent' />
                        </svg>
                        без сервисного сбора
                      </p>
                    )}
                    <p>
                      <svg
                        css={{
                          width: 20,
                          height: 20,
                          fill: '#fff',
                        }}
                      >
                        <use xlinkHref='#lock' />
                      </svg>
                      персональные данные защищены
                    </p>
                    {actionData.payment_on_spot && (
                      <p>
                        <svg
                          css={{
                            width: 20,
                            height: 20,
                            fill: '#fff',
                          }}
                        >
                          <use xlinkHref='#free_cancel' />
                        </svg>
                        бесплатная отмена бронирования
                      </p>
                    )}
                  </div>
                  <div
                    css={{
                      padding: '21px 0',
                    }}
                  >
                    <CustomCheckBox
                      form
                      value='buy-ticket-check'
                      callback={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheck(e.target.checked);
                      }}
                      label='подписаться на рассылку'
                      checked={check}
                    />
                  </div>
                  <p
                    css={{
                      color: '#7D7B7A',
                      textAlign: 'center',
                      padding: '14px 0',
                      a: {
                        textDecoration: 'underline',
                        '&:hover': {
                          textDecoration: 'none',
                        },
                      },
                    }}
                  >
                    Нажимая на кнопку{' '}
                    {actionData.type === ACTION_TYPES.certificate
                      ? '"Купить"'
                      : actionData.reservation_type ===
                        RESERVATION_TYPES.shortened
                      ? '"Оставить заявку"'
                      : '"Забронировать"'}
                    , вы соглашаетесь с{' '}
                    <a href='/pages/publichnaya-oferta/' target='_blank'>
                      Договором оферты
                    </a>{' '}
                    и{' '}
                    <a
                      href='/pages/polzovatelskoe-soglashenie/'
                      target='_blank'
                    >
                      Пользовательским соглашением
                    </a>
                  </p>
                  <div css={{ display: 'flex', justifyContent: 'center' }}>
                    {actionData.type === ACTION_TYPES.certificate ? (
                      <RedButton
                        disabled={
                          !isValid ||
                          (isCertificateTypeWithOpenDate
                            ? !Object.values(orderTickets).find((i) => i > 0)
                            : !selectedDate ||
                              !Object.values(orderTickets).find((i) => i > 0))
                        }
                        callback={() => {
                          /* eslint no-underscore-dangle: ["error", { "allow": ["_tmr"] }] */
                          _tmr.push({
                            type: 'reachGoal',
                            id: 3408105,
                            goal: 'book_click',
                          });
                          if (bankSelect) {
                            choosePayment();
                          } else {
                            onSubmit();
                          }
                        }}
                      >
                        {bankSelect ? 'Выбрать способ оплаты' : 'Купить'}
                      </RedButton>
                    ) : (
                      <RedButton
                        disabled={
                          !isValid ||
                          (shortReservation
                            ? false
                            : !selectedDate ||
                              !Object.values(orderTickets).find((i) => i > 0))
                        }
                        callback={() => {
                          /* eslint no-underscore-dangle: ["error", { "allow": ["_tmr"] }] */
                          _tmr.push({
                            type: 'reachGoal',
                            id: 3408105,
                            goal: 'book_click',
                          });
                          onSubmit();
                        }}
                      >
                        {actionData.type === ACTION_TYPES.FREE && <>Посетить</>}
                        {actionData.type === ACTION_TYPES.reservation && (
                          // eslint-disable-next-line react/jsx-no-useless-fragment
                          <>
                            {actionData.reservation_type ===
                            RESERVATION_TYPES.shortened
                              ? 'Оставить заявку'
                              : 'Забронировать'}
                          </>
                        )}
                      </RedButton>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {bank && (
              <div
                css={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#fff',
                  background:
                    'linear-gradient(110deg, #fff 8%, #ececec 18%, #fff 33%)',
                  borderRadius: 5,
                  backgroundSize: '200% 100%',
                  animation: '1.5s shine linear infinite',
                  opacity: 0.5,
                  zIndex: 99999,
                }}
              />
            )}
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                maxWidth: 760,
                minWidth: '100%',
                '.payment-button': {
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 16,
                  border: '1px solid rgba(0, 0, 0, 0.2)',
                  borderRadius: 12,
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    alignItems: 'flex-start',
                    padding: 12,
                    gap: 20,
                  },
                },
                '.payment-button-title': {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                },
                '.payment-button-title-heading': {
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1.1,
                  fontFamily: 'Montserrat',
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    fontSize: 16,
                    lineHeight: 1.3,
                  },
                },
                '.payment-button-title-description': {
                  fontSize: 16,
                  fontWeight: 500,
                  lineHeight: 1.2,
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontFamily: 'Montserrat',
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    fontSize: 14,
                  },
                },
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  marginTop: 30,
                  marginLeft: -10,
                  marginRight: -10,
                },
              }}
            >
              <button
                type='button'
                className='payment-button'
                onClick={() => setBank('sber')}
              >
                <span className='payment-button-title'>
                  <span className='payment-button-title-heading'>
                    Банковская карта
                  </span>
                  <span className='payment-button-title-description'>
                    Оплата банковской картой
                  </span>
                </span>

                <PaymentMarksSVG
                  css={{
                    width: '100%',
                    maxWidth: 125,
                    height: 16,
                  }}
                />
              </button>

              <button
                type='button'
                className='payment-button'
                onClick={() => setBank('tinkoff')}
              >
                <span className='payment-button-title'>
                  <span className='payment-button-title-heading'>
                    Тинькофф Касса
                  </span>
                  <span className='payment-button-title-description'>
                    Для владельцев банковской карты Тинькофф
                  </span>
                </span>

                <TinkoffSVG
                  css={{
                    width: '100%',
                    maxWidth: 129,
                    height: 32,
                    [DEVICE_SCREENS_MAX_STRING.md]: {
                      maxWidth: 103,
                      height: 24,
                    },
                  }}
                />
              </button>
            </div>
          </>
        )}
      </ModalContent>
    </ModalContentWrapper>
  );
}
